import React, { useState, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { ChevronLeft, ChevronRight, Download, Trash2, Maximize2, X } from 'lucide-react';
import { supabase } from '../lib/supabase';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

interface FileViewerProps {
  file: {
    id: string;
    filename: string;
    type: string;
    metadata: {
      path: string;
      type: string;
    };
  };
  onDelete: (id: string) => void;
}

export function FileViewer({ file, onDelete }: FileViewerProps) {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [loading, setLoading] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    const fetchImageUrl = async () => {
      try {
        const { data } = await supabase.storage
          .from('case-files')
          .createSignedUrl(file.metadata.path, 3600); // 1 hour expiry

        if (data?.signedUrl) {
          setImageUrl(data.signedUrl);
        }
      } catch (error) {
        console.error('Error getting signed URL:', error);
        setImageError(true);
      }
    };

    if (!isPDF) {
      fetchImageUrl();
    }
  }, [file.metadata.path]);

  const handleDownload = async () => {
    try {
      const { data, error } = await supabase.storage
        .from('case-files')
        .download(file.metadata.path);

      if (error) throw error;

      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.filename;
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this file?')) return;

    setLoading(true);
    try {
      const { error: storageError } = await supabase.storage
        .from('case-files')
        .remove([file.metadata.path]);

      if (storageError) throw storageError;

      const { error: dbError } = await supabase
        .from('documents')
        .delete()
        .eq('id', file.id);

      if (dbError) throw dbError;

      onDelete(file.id);
    } catch (error) {
      console.error('Error deleting file:', error);
    } finally {
      setLoading(false);
    }
  };

  const isPDF = file.metadata.type === 'application/pdf';

  const handleImageLoad = () => {
    setImageLoaded(true);
    setImageError(false);
  };

  const handleImageError = () => {
    setImageError(true);
    setImageLoaded(false);
  };

  const renderContent = () => {
    if (isPDF) {
      return (
        <div className="space-y-4">
          <Document
            file={imageUrl}
            onLoadSuccess={({ numPages }) => setNumPages(numPages)}
            loading={<div className="text-center py-4">Loading PDF...</div>}
          >
            <Page
              pageNumber={pageNumber}
              width={isFullscreen ? 800 : 400}
              loading={<div className="text-center py-4">Loading page...</div>}
            />
          </Document>
          {numPages && numPages > 1 && (
            <div className="flex items-center justify-center space-x-4">
              <button
                onClick={() => setPageNumber(p => Math.max(1, p - 1))}
                disabled={pageNumber <= 1}
                className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-50"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <span className="text-sm text-gray-600">
                Page {pageNumber} of {numPages}
              </span>
              <button
                onClick={() => setPageNumber(p => Math.min(numPages, p + 1))}
                disabled={pageNumber >= numPages}
                className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-50"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          )}
        </div>
      );
    }

    return (
      <div className={`relative ${isFullscreen ? 'h-[80vh]' : 'h-96'} bg-gray-100 rounded-lg overflow-hidden`}>
        {!imageLoaded && !imageError && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
          </div>
        )}
        {imageError && (
          <div className="absolute inset-0 flex items-center justify-center text-red-500">
            Failed to load image
          </div>
        )}
        {imageUrl && (
          <img
            src={imageUrl}
            alt={file.filename}
            className={`w-full h-full object-contain transition-opacity duration-300 ${
              imageLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            onLoad={handleImageLoad}
            onError={handleImageError}
            onClick={() => !isFullscreen && setIsFullscreen(true)}
          />
        )}
      </div>
    );
  };

  return (
    <>
      <div className="border rounded-lg overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow">
        <div className="bg-gray-50 px-4 py-2 flex justify-between items-center border-b">
          <span className="text-sm font-medium text-gray-700 truncate max-w-[200px]">
            {file.filename}
          </span>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setIsFullscreen(true)}
              className="p-1 text-gray-500 hover:text-gray-700"
              title="Fullscreen"
            >
              <Maximize2 className="h-4 w-4" />
            </button>
            <button
              onClick={handleDownload}
              className="p-1 text-gray-500 hover:text-gray-700"
              title="Download"
            >
              <Download className="h-4 w-4" />
            </button>
            <button
              onClick={handleDelete}
              disabled={loading}
              className="p-1 text-red-500 hover:text-red-700"
              title="Delete"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="p-4">
          {renderContent()}
        </div>
      </div>

      {/* Fullscreen Modal */}
      {isFullscreen && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50">
          <div className="relative max-w-7xl mx-auto p-4 w-full h-full flex items-center justify-center">
            <button
              onClick={() => setIsFullscreen(false)}
              className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
            >
              <X className="h-6 w-6" />
            </button>
            <div className="w-full h-full flex items-center justify-center">
              {renderContent()}
            </div>
          </div>
        </div>
      )}
    </>
  );
}